import { useState } from 'react';
import type { ArtAspect, CoverArtStyle, MediaItem } from '@icetus/contracts';
import { Button } from '@icetus/ui';
import { ImageUploader } from './image-uploader';
import { MediaPickerDialog } from './media-picker';
import { ArtGenerator, SparkIcon } from './art-generator';

/**
 * 一张图片的输入框：预览 + 地址 + 「媒体库」+「上传」。
 *
 * 存在理由：同一件事（换一张图）在控制台里有 15 个地方要做，而它们各自
 * 长成了"贴一个 URL"或"传一个文件"两种半成品。客户手上要么是相机原图、
 * 要么是上周刚传过的一张，而这两条路原来都不通向"已经传过的那张"。
 *
 * 两条不可妥协的规矩：
 *
 * 1. **手填地址必须留着**。外贸客户常把图放在自己的 CDN / 阿里云 OSS 上，
 *    强制只能从我们的媒体库选，等于逼他先传一份副本 —— 那不是改进。
 * 2. **手改地址要断开媒体引用**。分类封面记了 `imageMediaId`，客户把地址
 *    改成外链却还保留旧的 mediaId 时，服务端会认为这张封面仍然源自媒体库，
 *    将来清理任务删那张图会把他的封面置空。所以 `onPickItem(null)` 在
 *    手输时必须被调用。
 *
 * ## AI 生成挂在哪儿、为什么是可选参数
 *
 * 传了 `art` 才有那个按钮。**不给所有图片位置一个默认入口**：`logo-wall`
 * 要的是客户的真 logo、`partner-cards` 要的是真工厂照，那些地方放一个
 * "AI 生成"等于鼓励伪造证据。所以按钮的开关由**区块字段描述符**的
 * `artAspect` 决定（见 `packages/blocks/src/protocol.ts`），而不是这里。
 */

export interface ImageFieldProps {
  value: string;
  onChange: (url: string) => void;
  /** 选中/改地址时一并告知来源媒体；手输或清空时为 null */
  onPickItem?: (item: MediaItem | null) => void;
  /** 缺这两个就只渲染文本框（单测与"没有站点上下文"的场景） */
  tenantId?: string;
  siteId?: string;
  placeholder?: string;
  disabled?: boolean;
  /** 预览框形状：横版给横幅/Logo，方形给图标/封面 */
  ratio?: 'wide' | 'square';
  /** 空位时的说明，例如「未上传」 */
  emptyLabel?: string;
  /** 上传按钮文案 */
  uploadLabel?: string;
  /** 与区块 schema 的 maxLength 对齐；超长地址保存时会被服务端拒，提前挡一下 */
  maxLength?: number;
  /**
   * 是否显示「移除」。区块属性面板里外层 `FieldShell` 已经有一个「清空」
   * （可空字段才有），这里再给一个就是同一件事出现两次。
   */
  showRemove?: boolean;
  /**
   * 给这一格接上 AI 生图。不给就没有那个按钮。
   *
   * `aspect` 必须由调用方给准 —— 它决定模型按什么形状构图、交付按什么比例裁。
   * 传错不会报错，只会交一张裁掉了主体的图，所以这个值只从区块字段描述符里来。
   */
  art?: {
    tenantId: string;
    siteId: string;
    aspect: ArtAspect;
    /** 打开面板时预填的"画什么"（分类封面用分类名，区块里的图留空） */
    subjectHint?: string;
    /** 默认画法（商品图给 `product`，不给就是场景照） */
    defaultStyle?: CoverArtStyle;
    /** 面板标题里这块地方的名字（"商品图"这种），不给按比例推 */
    noun?: string;
  };
}

export function ImageField({
  value,
  onChange,
  onPickItem,
  tenantId,
  siteId,
  placeholder = 'https://… 或从媒体库选择',
  disabled = false,
  ratio = 'wide',
  emptyLabel = '无图',
  uploadLabel = '上传',
  maxLength = 500,
  showRemove = true,
  art,
}: ImageFieldProps) {
  const [picking, setPicking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const canPick = Boolean(tenantId && siteId) && !disabled;
  const canGenerate = canPick && Boolean(art) && !disabled;

  const setFromMedia = (item: MediaItem) => {
    onChange(item.url);
    onPickItem?.(item);
  };

  return (
    <div className="imgfield">
      <div className={`imgfield__preview imgfield__preview--${ratio}`}>
        {value ? (
          <img src={value} alt="" loading="lazy" />
        ) : (
          <span className="imgfield__empty">{emptyLabel}</span>
        )}
      </div>
      <div className="imgfield__body">
        <input
          className="field"
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            // 地址被手动改过：这张图不再"来自媒体库"
            onPickItem?.(null);
          }}
        />
        <div className="imgfield__actions">
          {canPick ? (
            <Button variant="ghost" disabled={disabled} onClick={() => setPicking(true)}>
              从媒体库选择
            </Button>
          ) : null}
          {canPick ? (
            <ImageUploader
              tenantId={tenantId!}
              siteId={siteId!}
              multiple={false}
              disabled={disabled}
              label={uploadLabel}
              onUploaded={(items) => {
                const item = items[0];
                if (item) setFromMedia(item);
              }}
            />
          ) : null}
          {canGenerate ? (
            <Button
              variant="primary"
              className="btn-ai"
              disabled={disabled}
              onClick={() => setGenerating(true)}
            >
              <SparkIcon />
              {'用 AI 生成'}
            </Button>
          ) : null}
          {showRemove && value && !disabled ? (
            <Button
              variant="ghost"
              onClick={() => {
                onChange('');
                onPickItem?.(null);
              }}
            >
              移除
            </Button>
          ) : null}
        </div>
      </div>

      {canPick ? (
        <MediaPickerDialog
          open={picking}
          tenantId={tenantId!}
          siteId={siteId!}
          onClose={() => setPicking(false)}
          onConfirm={(items) => {
            setPicking(false);
            const item = items[0];
            if (item) setFromMedia(item);
          }}
        />
      ) : null}

      {art && canGenerate ? (
        <ArtGenerator
          open={generating}
          onClose={() => setGenerating(false)}
          tenantId={art.tenantId}
          siteId={art.siteId}
          aspect={art.aspect}
          defaultSubject={art.subjectHint ?? ''}
          defaultStyle={art.defaultStyle ?? 'scene'}
          noun={art.noun}
          onApply={({ imageUrl }) => {
            /*
             * 只回填地址，不碰 `onPickItem`。
             *
             * 区块里的图只存一个 URL（schema 里就是字符串），没有 mediaId 这一层；
             * 而分类封面那种要记 mediaId 的落点，走的是它自己的面板，不经过这里。
             * 顺手 `onPickItem(null)` 反而更对：AI 出的图对"这条引用源自媒体库哪一项"
             * 来说确实没有对应项，留着上一次的 mediaId 会指错。
             */
            onChange(imageUrl);
            onPickItem?.(null);
          }}
        />
      ) : null}
    </div>
  );
}
